import React from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams, createSearchParams } from "react-router-dom";
import { Header } from "../../../components/header";
import { Loading } from "../../../components/loading";
import { EmptyList } from "../../../components/empty-list";
import { Select, Input, SearchBar } from "../../../components";
import { Table, TableHeader, TableBody, TableRow, TableCell } from "../../../components/tables";
import { SendIcon } from "../../../components/icons";
import { useCustomerAddresses } from "../hooks";
import { CustomerSelectSearch } from "../../customers/components";
import { cleanObject } from "../../../helpers/clean-object";
import { AddressType } from "../../../__generated__/globalTypes";
import { addressTypeLabel } from "../../../helpers/enum-labels";

interface ISearchForm {
  search?: string;
  type?: string;
  active?: string;
  customerId?: string;
  city?: string;
  postal?: string;
}

export const CustomerAddresses: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const form = useForm<ISearchForm>({
    mode: "all",
    defaultValues: {
      search: searchParams.get("search") ?? "",
      type: searchParams.get("type") ?? "",
      active: searchParams.get("active") ?? "",
      customerId: searchParams.get("customerId") ?? "",
      city: searchParams.get("city") ?? "",
      postal: searchParams.get("postal") ?? "",
    },
  });

  const onSubmit = () => {
    const values = form.getValues();
    navigate({
      pathname: "/customer-addresses",
      search: `?${createSearchParams(cleanObject(values))}`,
    });
  };

  const where: any = {};
  const search = searchParams.get("search");
  const type = searchParams.get("type");
  const active = searchParams.get("active");
  const customerId = searchParams.get("customerId");
  if (search) where.search = search;
  if (type) where.type = type;
  if (active) where.active = active === "true";
  if (customerId) where.customerId = +customerId;

  const city = searchParams.get("city");
  const postal = searchParams.get("postal");

  const { data, loading } = useCustomerAddresses({ where });

  const results = (data?.customerAddresses?.results ?? []).filter((address) => {
    if (city && !address.city?.toLowerCase().includes(city.toLowerCase())) return false;
    if (postal && !address.postal?.toLowerCase().includes(postal.toLowerCase())) return false;
    return true;
  });

  return (
    <>
      <Header
        title="Adresses"
        subtitle="Liste des adresses clients"
        buttons={[
          {
            actionText: "Nouvelle adresse",
            bgColor: "indigo",
            textColor: "white",
            link: `/customer-address/create`,
            icon: <SendIcon />,
          },
        ]}
      />

      <div className="main-container">
        <SearchBar
          form={form}
          onSubmit={onSubmit}
          placeholder="Rechercher une adresse..."
          advancedFilters={
            <>
              <CustomerSelectSearch form={form} name="customerId" />
              <Select label="Type" className="w-full" {...form.register("type")}>
                <option value="">Tous</option>
                {Object.keys(AddressType).map((type) => (
                  <option key={type} value={type}>
                    {addressTypeLabel(type)}
                  </option>
                ))}
              </Select>
              <Select label="Active" className="w-full" {...form.register("active")}>
                <option value="">Toutes</option>
                <option value="true">Oui</option>
                <option value="false">Non</option>
              </Select>
              <Input
                label="Ville"
                className="w-full"
                {...form.register("city")}
                placeholder="Paris"
              />
              <Input
                label="Code postal"
                className="w-full"
                {...form.register("postal")}
                placeholder="75001"
              />
            </>
          }
        />

        <div className="p-4 mb-6 bg-white shadow rounded overflow-x-auto">
          {loading ? (
            <Loading />
          ) : !results.length ? (
            <EmptyList text="Aucune adresse" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow hover={false}>
                  <TableCell header>Type</TableCell>
                  <TableCell header>Libellé</TableCell>
                  <TableCell header>Ville</TableCell>
                  <TableCell header align="center">
                    Active
                  </TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {results.map((address) => (
                  <TableRow
                    key={`customer-address-${address.id}`}
                    onClick={() => navigate(`/customer-address/${address.id}`)}
                  >
                    <TableCell>{addressTypeLabel(address.type)}</TableCell>
                    <TableCell>{address.label}</TableCell>
                    <TableCell>{address.city}</TableCell>
                    <TableCell align="center">
                      {address.active ? "Oui" : "Non"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </>
  );
};
